import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { makeStyles } from 'tss-react/mui';
import { useMemo } from 'react';

import type { LeaveEntry, TeamMember } from '../data/model';
import {
  LEAVE_TYPE_BY_ID,
  formatLeaveRange,
  isLeaveOnDay,
  leaveDurationDays,
  startOfToday,
  toIso,
} from '../data/model';
import { fonts, themeTokens } from '../theme';
import { MemberAvatar, TypeBadge } from './common';

const useStyles = makeStyles()(theme => {
  const palette = themeTokens(theme);
  return {
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: 16,
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      border: `1px solid ${palette.border}`,
      borderRadius: 12,
      backgroundColor: palette.surface,
      padding: 20,
      transition:
        'border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease',
      '&:hover': {
        borderColor: palette.borderStrong,
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(26, 35, 50, 0.08)',
      },
    },
    header: { display: 'flex', alignItems: 'center', gap: 12 },
    identity: { minWidth: 0, flex: 1 },
    name: {
      fontFamily: fonts.body,
      fontSize: 16,
      fontWeight: 700,
      color: palette.navy,
      letterSpacing: '-0.01em',
    },
    onLeaveBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '3px 10px',
      borderRadius: 999,
      backgroundColor: palette.terracotta,
      color: palette.onAccent,
      fontFamily: fonts.mono,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    },
    badgeDot: {
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: palette.onAccent,
    },
    sectionLabel: {
      fontFamily: fonts.mono,
      fontSize: 10,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: palette.muted,
      margin: '18px 0 10px',
    },
    upcomingRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      padding: '7px 0',
      borderTop: `1px solid ${palette.border}`,
    },
    upcomingDate: {
      fontFamily: fonts.mono,
      fontSize: 12,
      color: palette.navy,
      whiteSpace: 'nowrap',
    },
    upcomingRight: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 4,
      border: 'none',
      background: 'transparent',
      color: palette.muted,
      cursor: 'pointer',
      borderRadius: 6,
      '&:hover': { color: palette.navy, backgroundColor: palette.paper },
      '& svg': { fontSize: 16 },
    },
    deleteButton: {
      '&:hover': { color: palette.terracotta, backgroundColor: palette.paper },
    },
    noUpcoming: {
      fontFamily: fonts.body,
      fontSize: 13,
      color: palette.muted,
      padding: '4px 0',
    },
    footer: {
      display: 'flex',
      gap: 12,
      marginTop: 'auto',
      paddingTop: 16,
    },
    stat: {
      flex: 1,
      backgroundColor: palette.paper,
      borderRadius: 8,
      padding: '10px 12px',
    },
    statValue: {
      fontFamily: fonts.mono,
      fontSize: 20,
      fontWeight: 600,
      color: palette.navy,
      lineHeight: 1.1,
    },
    statLabel: {
      fontFamily: fonts.mono,
      fontSize: 10,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      color: palette.muted,
      marginTop: 4,
    },
    spacer: { marginTop: 12 },
    emptyState: {
      fontFamily: fonts.body,
      fontSize: 14,
      color: palette.muted,
      border: `1px solid ${palette.border}`,
      borderRadius: 12,
      backgroundColor: palette.surface,
      padding: '48px 24px',
      textAlign: 'center',
      maxWidth: 460,
      margin: '0 auto',
    },
  };
});

export const TeamOverviewTab = ({
  leaves,
  members,
  currentUserRef,
  onEditLeave,
  onDeleteLeave,
}: {
  leaves: LeaveEntry[];
  members: TeamMember[];
  currentUserRef: string;
  onEditLeave: (leave: LeaveEntry) => void;
  onDeleteLeave: (leave: LeaveEntry) => void;
}) => {
  const { classes } = useStyles();
  const todayIso = toIso(startOfToday());
  const normalizedUserRef = currentUserRef.toLocaleLowerCase('en-US');

  const byMember = useMemo(() => {
    const map = new Map<string, LeaveEntry[]>();
    for (const member of members) {
      map.set(
        member.id,
        leaves
          .filter(leave => leave.memberId === member.id)
          .sort((a, b) => a.startDate.localeCompare(b.startDate)),
      );
    }
    return map;
  }, [leaves, members]);

  if (members.length === 0) {
    return (
      <div className={classes.emptyState}>
        This group has no members yet. Add members to the group in the catalog
        to start tracking their leave.
      </div>
    );
  }

  return (
    <div className={classes.grid}>
      {members.map(member => {
        const memberLeaves = byMember.get(member.id) ?? [];
        const isCurrentUser =
          member.id.toLocaleLowerCase('en-US') === normalizedUserRef;
        const onLeaveNow = memberLeaves.some(leave =>
          isLeaveOnDay(leave, todayIso),
        );
        const upcoming = memberLeaves.filter(
          leave => leave.endDate >= todayIso,
        );
        const totalDays = memberLeaves.reduce(
          (sum, leave) => sum + leaveDurationDays(leave),
          0,
        );

        return (
          <div key={member.id} className={classes.card}>
            <div className={classes.header}>
              <MemberAvatar member={member} size={44} />
              <div className={classes.identity}>
                <div className={classes.name}>{member.name}</div>
              </div>
              {onLeaveNow && (
                <span className={classes.onLeaveBadge}>
                  <span className={classes.badgeDot} />
                  On Leave Now
                </span>
              )}
            </div>

            <div className={classes.sectionLabel}>Upcoming leaves</div>
            {upcoming.length === 0 ? (
              <div className={classes.noUpcoming}>Nothing scheduled.</div>
            ) : (
              upcoming.map(leave => (
                <div key={leave.id} className={classes.upcomingRow}>
                  <span className={classes.upcomingDate}>
                    {formatLeaveRange(leave)}
                  </span>
                  <span className={classes.upcomingRight}>
                    <TypeBadge type={LEAVE_TYPE_BY_ID[leave.type]} />
                    {isCurrentUser && (
                      <>
                        <button
                          type="button"
                          className={classes.actionButton}
                          aria-label={`Edit leave ${formatLeaveRange(leave)}`}
                          onClick={() => onEditLeave(leave)}
                        >
                          <EditOutlinedIcon />
                        </button>
                        <button
                          type="button"
                          className={`${classes.actionButton} ${classes.deleteButton}`}
                          aria-label={`Delete leave ${formatLeaveRange(leave)}`}
                          onClick={() => onDeleteLeave(leave)}
                        >
                          <DeleteOutlineIcon />
                        </button>
                      </>
                    )}
                  </span>
                </div>
              ))
            )}

            <div className={classes.footer}>
              <div className={classes.stat}>
                <div className={classes.statValue}>{totalDays}</div>
                <div className={classes.statLabel}>Total leave days</div>
              </div>
              <div className={classes.stat}>
                <div className={classes.statValue}>{upcoming.length}</div>
                <div className={classes.statLabel}>Upcoming</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
